/**
 * Conflict Resolution Modal
 *
 * Shown when user logs in and has BOTH local and cloud data.
 * Displays the difference and lets user choose how to resolve:
 * 1. Keep cloud data (discard local)
 * 2. Replace cloud with local data
 * 3. Merge (add local data to cloud)
 */

import { useState } from 'react';
import { Icons } from './Icons';
import { migrationService, MigrationProgress, DataComparison } from '../services/migrationService';
import { StorageService } from '../services/storage';
import { useConfirmation } from '../hooks/useConfirmation';
import { ConfirmationModal } from './ui/ConfirmationModal';

interface ConflictResolutionModalProps {
  isOpen: boolean;
  userId: string;
  comparison: DataComparison;
  onComplete: () => void;
}

export function ConflictResolutionModal({
  isOpen,
  userId,
  comparison,
  onComplete,
}: ConflictResolutionModalProps) {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<MigrationProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { confirm, confirmationProps } = useConfirmation();

  if (!isOpen) return null;

  const handleKeepCloud = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('☁️ User chose to keep cloud data...');
      await migrationService.resolveConflicts(userId, 'cloud_wins');

      StorageService.set('sync_choice_made', true);
      window.location.reload();
    } catch (err) {
      console.error('❌ Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleKeepLocal = async () => {
    const confirmed = await confirm({
      title: 'Remplacer les données cloud',
      message: 'Cela remplacera toutes vos données cloud par les données locales. Cette action est irréversible.',
      variant: 'danger',
      confirmText: 'Remplacer',
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      setError(null);

      console.log('💾 User chose to replace cloud with local...');
      const result = await migrationService.resolveConflicts(userId, 'local_wins');

      if (result.success) {
        StorageService.set('sync_choice_made', true);
        window.location.reload();
      } else {
        setError(`Erreur: ${result.errors.join(', ')}`);
      }
    } catch (err) {
      console.error('❌ Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔀 User chose to merge...');
      const result = await migrationService.mergeLocalToCloud(userId, setProgress);

      if (result.success) {
        console.log('✅ Merge successful, setting sync_choice_made...');
        StorageService.set('sync_choice_made', true);

        // Small delay to ensure storage is persisted before reload
        await new Promise(resolve => setTimeout(resolve, 500));

        // Clear service worker cache and force hard reload
        console.log('🔄 Clearing caches and forcing hard reload...');
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          console.log('🗑️ Cleared browser caches:', cacheNames);
        }

        // Unregister service workers to force fresh load
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(reg => reg.unregister()));
          console.log('🗑️ Unregistered service workers:', registrations.length);
        }

        // Force reload without cache
        window.location.href = window.location.pathname + '?_reload=' + Date.now();
      } else {
        console.error('❌ Merge failed:', result.errors);
        setError(`Erreur: ${result.errors.join(', ')}`);
      }
    } catch (err) {
      console.error('❌ Merge error:', err);
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '20px',
      }}
    >
      <div
        style={{
          background: 'var(--card-bg)',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'var(--warning-bg, rgba(245, 158, 11, 0.1))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.AlertTriangle size={24} style={{ color: 'var(--warning, #f59e0b)' }} />
          </div>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
              Données en conflit
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--muted)', margin: 0 }}>
              Des différences ont été détectées
            </p>
          </div>
        </div>

        {/* Comparison */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          {/* Cloud data */}
          <div
            style={{
              padding: '16px',
              background: 'var(--bg)',
              borderRadius: '12px',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Icons.Cloud size={18} style={{ color: 'var(--primary)' }} />
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>Cloud</span>
            </div>
            <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
              <p style={{ margin: '4px 0' }}>
                <strong>{comparison.cloud.tokensCount}</strong> tokens
              </p>
              <p style={{ margin: '4px 0' }}>
                <strong>{comparison.cloud.positionsCount}</strong> transactions
              </p>
            </div>
          </div>

          {/* Local data */}
          <div
            style={{
              padding: '16px',
              background: 'var(--bg)',
              borderRadius: '12px',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Icons.HardDrive size={18} style={{ color: 'var(--success, #10b981)' }} />
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>Local</span>
            </div>
            <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
              <p style={{ margin: '4px 0' }}>
                <strong>{comparison.local.tokensCount}</strong> tokens
              </p>
              <p style={{ margin: '4px 0' }}>
                <strong>{comparison.local.positionsCount}</strong> transactions
              </p>
            </div>
          </div>
        </div>

        {/* Delta */}
        {(comparison.delta.tokensOnlyLocal.length > 0 ||
          comparison.delta.positionsOnlyLocal > 0) && (
          <div
            style={{
              padding: '16px',
              background: 'var(--warning-bg, rgba(245, 158, 11, 0.1))',
              borderRadius: '12px',
              marginBottom: '24px',
            }}
          >
            <p
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--warning, #f59e0b)',
                margin: '0 0 8px 0',
              }}
            >
              Nouveautés locales non synchronisées :
            </p>
            {comparison.delta.tokensOnlyLocal.length > 0 && (
              <p style={{ fontSize: '14px', color: 'var(--text)', margin: '4px 0' }}>
                + {comparison.delta.tokensOnlyLocal.length} token(s) :{' '}
                <strong>{comparison.delta.tokensOnlyLocal.join(', ')}</strong>
              </p>
            )}
            {comparison.delta.positionsOnlyLocal > 0 && (
              <p style={{ fontSize: '14px', color: 'var(--text)', margin: '4px 0' }}>
                + {comparison.delta.positionsOnlyLocal} transaction(s)
              </p>
            )}
          </div>
        )}

        {/* Progress indicator */}
        {progress && (
          <div
            style={{
              padding: '16px',
              background: 'var(--bg)',
              borderRadius: '8px',
              marginBottom: '24px',
            }}
          >
            <p style={{ color: 'var(--primary)', fontSize: '14px', marginBottom: '8px' }}>
              {progress.stage}
            </p>
            <div
              style={{
                width: '100%',
                height: '8px',
                background: 'var(--border)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progress.percentage}%`,
                  height: '100%',
                  background: 'var(--primary)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              padding: '12px',
              background: 'var(--error-bg)',
              border: '1px solid var(--error)',
              borderRadius: '8px',
              marginBottom: '24px',
            }}
          >
            <p style={{ color: 'var(--error)', fontSize: '14px', margin: 0 }}>{error}</p>
          </div>
        )}

        {/* Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Option 1: Merge (recommended) */}
          <button
            onClick={handleMerge}
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              opacity: loading ? 0.7 : 1,
            }}
          >
            <Icons.GitMerge size={20} />
            {loading ? 'Fusion en cours...' : 'Fusionner (ajouter les données locales au cloud)'}
          </button>

          {/* Option 2: Keep cloud */}
          <button
            onClick={handleKeepCloud}
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              opacity: loading ? 0.5 : 1,
            }}
          >
            <Icons.Cloud size={20} />
            Garder uniquement le cloud (ignorer local)
          </button>

          {/* Option 3: Keep local (dangerous) */}
          <button
            onClick={handleKeepLocal}
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: 'transparent',
              color: 'var(--error, #ef4444)',
              border: '1px solid var(--error, #ef4444)',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              opacity: loading ? 0.5 : 1,
            }}
          >
            <Icons.AlertTriangle size={20} />
            Remplacer le cloud par le local
          </button>
        </div>

        {/* Info */}
        <p
          style={{
            fontSize: '12px',
            color: 'var(--muted)',
            marginTop: '20px',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          La fusion ajoutera vos nouveaux tokens locaux au cloud sans toucher aux données existantes.
        </p>
      </div>
      <ConfirmationModal {...confirmationProps} />
    </div>
  );
}
